import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {InputAdornment, TextField} from "@mui/material";
import LocationMap from "../LocationMap";
import MapStyle from "../MapStyle";
import {PinDrop} from "@mui/icons-material";
import {getFullAddressPartsFromPlace} from "../../util/AddressUtil";
import {styled} from "@mui/material/styles";

const google = window.google;

const AddressLookupTextField = styled(TextField)(() => ({
  marginBottom: '0.71rem'
}));

const AddressLookup = ({ name, value, onChange, label, placeholder, error = false, helperText = '' }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const setMarkerRef = useRef(null);
  const centerMapRef = useRef(null);

  const [mapContainer, setMapContainer] = useState(null);
  const [center, setCenter] = useState({ lat: 37.090240, lng: -95.712891 });
  const [markers, setMarkers] = useState([]);
  const [zoom, setZoom] = useState(4);

  const setMarkerFromPlace = useCallback((location) => {
    setMarkers([{ position: location, showInfo: false }]);
  }, []);

  const centerMapOnGeometry = useCallback((location, viewport) => {
    setCenter(location);
    if (mapContainer && viewport) {
      mapContainer.fitBounds(viewport);
    } else {
      setZoom(10);
    }
  }, [mapContainer]);

  // Keep refs current so the place_changed listener always uses the latest callbacks
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setMarkerRef.current = setMarkerFromPlace;
  }, [setMarkerFromPlace]);

  useEffect(() => {
    centerMapRef.current = centerMapOnGeometry;
  }, [centerMapOnGeometry]);

  useEffect(() => {
    if (value) {
      new google.maps.Geocoder().geocode(
        { 'address': value },
        (results, status) => {
          if (status === 'OK') {
            let location = results[0].geometry.location
            setMarkerFromPlace(location);
            centerMapOnGeometry(location, results[0].geometry.viewport);
          }
        }
      );
    }
  }, [value, setMarkerFromPlace, centerMapOnGeometry]);

  const onLoad = (mapContainer) => {
    setMapContainer(mapContainer)
  };

  const onUnmount = () => {
    setMapContainer(null);
  };

  // Initialize Google Places Autocomplete once after mount
  useEffect(() => {
    const inputEl = inputRef.current?.querySelector?.('input') || inputRef.current;
    if (!inputEl || autocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(inputEl);
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location || !place?.address_components?.length) {
        console.log("No details available for input: '" + place?.name + "'");
        return;
      }

      const addressParts = getFullAddressPartsFromPlace(place);
      const location = place.geometry.location;

      setMarkerRef.current(location);
      centerMapRef.current(location, place.geometry.viewport);
      onChangeRef.current({
        lookupLocation: place.formatted_address,
        ...addressParts,
        addressLatitude: location.lat(),
        addressLongitude: location.lng()
      });
    });

    return () => {
      google.maps.event.removeListener(listener);
      autocompleteRef.current = null;
    };
  }, []);

  const handleTextChange = (event) => {
    // User is typing but has not yet selected an address from the dropdown
    onChange({
      lookupLocation: event.target.value,
    });
  };

  return (
    <>
      <AddressLookupTextField
        variant='standard'
        ref={inputRef}
        name={name}
        value={value}
        label={label}
        onChange={handleTextChange}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PinDrop color={'action'} />
            </InputAdornment>
          )
        }}
      />
      <LocationMap
        onLoad={onLoad}
        onUnmount={onUnmount}
        center={center}
        zoom={zoom}
        options={{
          styles: MapStyle,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scrollwheel: false
        }}
        markers={markers}
      />
    </>
  );
};

AddressLookup.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string
};

export default AddressLookup;
